import React, { useEffect, useState } from "react";
import CardEmpty from "Utils/Cards/CardEmpty";
import { ListadoTrasladosApi } from "../../api/ListadoTrasladosApi";
import Paginacion from "../../../../../../Utils/Components/Paginacion/Paginacion";
import LoadMask from "../../../../../../Utils/LoadMask/LoadMask";
import { useForm } from "react-hook-form";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import { FiltrosTabs } from "../../../../components/FiltosTabs";
import { EnTransitoTable } from "./EnTransitoTable";
import { RecibidosTable } from "./RecibidosTable";
import { PendientesTable } from "./PendientesTable";
import { ModalIngresos } from "../../../../components/ModalIngresos";
import { useDebounce } from "../../../../../../Utils/Hooks/useDebounce";
import { Link } from "react-router-dom";
import {
    dateToDayEnd,
    dateToDayStart,
    getErrorFromResponse,
} from "../../../../../../../../utility/utils";
import { DecisionSwal } from "../../../../../../Utils/Utils/DecisionSwal";

export const TrasladosTab = ({ estado, storageKey, modo }) => {
    const STORAGE_KEY = storageKey;
    const savedState = JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    const filtros = savedState && savedState.filtros ? savedState.filtros : {};

    const searchDefault = filtros.search || "";
    const fechaInicialDefault = filtros.fecha_inicial
        ? moment(filtros.fecha_inicial)
        : null;

    const fechaFinalDefault = filtros.fecha_final
        ? moment(filtros.fecha_final)
        : null;

    const { control, watch } = useForm({
        defaultValues: {
            search: searchDefault,
            fecha_inicial: fechaInicialDefault,
            fecha_final: fechaFinalDefault,
        },
    });

    const [traslados, setTraslados] = useState([]);
    const [paginaSeleccionada, setPaginaSeleccionada] = useState(
        savedState.pagina || 1,
    );
    const [cantidadResultados, setCantidadResultados] = useState(0);

    const [trasladoSeleccionado, setTrasladoSeleccionado] = useState(null);

    const search = watch("search");
    const debouncedSearch = useDebounce(search, 500);
    const fecha_inicial = watch("fecha_inicial");
    const fecha_final = watch("fecha_final");

    const {
        getTraslados,
        actualizarTraslado,
        eliminarTrasladoPendiente,
        cargando,
    } = ListadoTrasladosApi();

    const GetTraslados = async () => {
        try {
            const params = {
                page: paginaSeleccionada,
                estado: estado,
            };

            if (debouncedSearch) params.search = debouncedSearch;

            if (fecha_inicial) {
                params.fecha_inicial = dateToDayStart(fecha_inicial);
            }

            if (fecha_final) {
                params.fecha_final = dateToDayEnd(fecha_final);
            }

            const response = await getTraslados(params);
            setTraslados(response.results);
            setCantidadResultados(response.count);
        } catch (error) {
            // Si el error es por una página inválida, volvemos a la página anterior
            if (
                error &&
                error.detail === "Invalid page." &&
                paginaSeleccionada > 1
            ) {
                setPaginaSeleccionada((prev) => prev - 1);
                return;
            }

            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener traslados",
            );
            NotificationManager.error(errorMsg, "Error", 3000);

            setTraslados([]);
            setCantidadResultados(0);
        }
    };

    const MarcarComoRecibido = async (trasladoId) => {
        try {
            await actualizarTraslado(trasladoId, {
                estado: "recibido",
                fecha_recepcion: new Date().toISOString(),
            });

            NotificationManager.success(
                "Traslado marcado como recibido",
                "Éxito",
                3000,
            );

            await GetTraslados();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al marcar como recibido",
            );

            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const EliminarTrasladoPendiente = async (trasladoId) => {
        try {
            await eliminarTrasladoPendiente(trasladoId);

            NotificationManager.success("Traslado eliminado", "Éxito", 3000);
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al eliminar traslado",
            );

            NotificationManager.error(errorMsg, "Error", 3000);
        } finally {
            await GetTraslados();
        }
    };

    const MostrarMensajeEliminacionTraslado = (trasladoId) => {
        let mensaje =
            "¿Desea eliminar este traslado? Se eliminará el traslado y no se podrá recuperar la información.";

        DecisionSwal(mensaje, "Sí", "No", () =>
            EliminarTrasladoPendiente(trasladoId),
        );
    };

    useEffect(() => {
        GetTraslados();
    }, [paginaSeleccionada, debouncedSearch, fecha_inicial, fecha_final]);

    useEffect(() => {
        const stateToSave = {
            filtros: {
                search: debouncedSearch,
                fecha_inicial,
                fecha_final,
            },
            pagina: paginaSeleccionada,
        };

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    }, [debouncedSearch, fecha_inicial, fecha_final, paginaSeleccionada]);

    const estadoPendiente = estado === "pendiente";

    const renderTable = () => {
        if (estadoPendiente) {
            return (
                <PendientesTable
                    traslados={traslados}
                    MostrarMensajeEliminacionTraslado={
                        MostrarMensajeEliminacionTraslado
                    }
                />
            );
        } else if (estado === "en_transito") {
            return (
                <EnTransitoTable
                    traslados={traslados}
                    marcarComoRecibido={MarcarComoRecibido}
                    setTrasladoSeleccionado={setTrasladoSeleccionado}
                    modo={modo}
                />
            );
        } else if (estado === "recibido") {
            return (
                <RecibidosTable
                    traslados={traslados}
                    setTrasladoSeleccionado={setTrasladoSeleccionado}
                    modo={modo}
                />
            );
        } else {
            return <div>Estado desconocido</div>;
        }
    };

    return (
        <div className="listado-traslados__tab-wrapper">
            <LoadMask loading={cargando} blur>
                <CardEmpty noShadow>
                    <div className="listado-traslados__table-header">
                        <FiltrosTabs
                            control={control}
                            onRefresh={GetTraslados}
                            resetPage={() => setPaginaSeleccionada(1)}
                            searchPlaceholder={
                                estadoPendiente
                                    ? "Número de traslado"
                                    : "Número, recepción, finca"
                            }
                        />
                        {estadoPendiente && (
                            <Link to={`crear-traslado`} className="px-2">
                                <button className="btn btn-primary">
                                    Crear Traslado
                                </button>
                            </Link>
                        )}
                    </div>

                    {renderTable()}
                    <Paginacion
                        cantidadResultados={cantidadResultados}
                        paginaSeleccionada={paginaSeleccionada}
                        setPaginaSeleccionada={setPaginaSeleccionada}
                    />
                </CardEmpty>
            </LoadMask>
            {trasladoSeleccionado && (
                <ModalIngresos
                    opened={!!trasladoSeleccionado}
                    onClose={() => setTrasladoSeleccionado(null)}
                    trasladoSeleccionado={trasladoSeleccionado}
                />
            )}
        </div>
    );
};
